import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VisionViewer from './VisionViewer';
import VisionCreator from './VisionCreator';
import { useAppContext } from '../../AppContext';
import './VisionsPanel.css';
import * as LucideIcons from 'lucide-react';

function VisionsPanel({ visions, handleUserSelect }) {
    const { t } = useTranslation();
    const { currentUserProfile, user } = useAppContext();
    const [selectedVisionIndex, setSelectedVisionIndex] = useState(null);
    const [creatorOpen, setCreatorOpen] = useState(false);

    const currentUserPhoto = currentUserProfile?.photoURL || user?.photoURL || 'https://placehold.co/40x40/AEC6CF/FFFFFF?text=U';

    const openViewer = (index) => {
        setSelectedVisionIndex(index);
    };

    const closeViewer = () => {
        setSelectedVisionIndex(null);
    };

    const handleNextUser = () => {
        if (selectedVisionIndex !== null) {
            setSelectedVisionIndex((prevIndex) => (prevIndex + 1) % visions.length);
        }
    };

    const handlePrevUser = () => {
        if (selectedVisionIndex !== null) {
            setSelectedVisionIndex((prevIndex) => (prevIndex - 1 + visions.length) % visions.length);
        }
    };

    return (
        <>
            <div className="story-reel-container bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-2xl p-4 mb-6 w-full overflow-hidden select-none">
                <div className="story-reel-scroll flex items-center space-x-5 overflow-x-auto">
                    {/* Your Vision Creator Shortcut */}
                    <div className="story-reel-item flex flex-col items-center flex-shrink-0 cursor-pointer group" onClick={() => setCreatorOpen(true)}>
                        <div className="relative w-16 h-16 rounded-full p-[2px] border border-gray-700/60 group-hover:border-cyan-400 transition-all mb-1.5 flex items-center justify-center bg-slate-950">
                            <img 
                                src={currentUserPhoto} 
                                alt="Your Vision" 
                                className="w-full h-full rounded-full object-cover" 
                            />
                            <div className="absolute bottom-0 right-0 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center border-2 border-slate-900 shadow-md transform group-hover:scale-110 transition-transform">
                                <LucideIcons.Plus size={12} className="stroke-[3]" />
                            </div>
                        </div>
                        <span className="text-[11px] font-medium text-gray-400 group-hover:text-white transition-colors truncate max-w-[72px]">
                            {t('visions.panel.yourStory', 'Your Vision')}
                        </span>
                    </div>

                    {/* Active Stories from connections */}
                    {visions.map((vision, index) => {
                        // Skip if the vision has no items
                        if (!vision.items || vision.items.length === 0) return null;
                        
                        return (
                            <div 
                                key={vision.id} 
                                className="story-reel-item flex flex-col items-center flex-shrink-0 cursor-pointer group" 
                                onClick={() => openViewer(index)}
                            >
                                <div className="story-ring-wrapper mb-1.5 flex items-center justify-center bg-slate-950">
                                    <img 
                                        src={vision.userPhotoURL || 'https://placehold.co/40x40/AEC6CF/FFFFFF?text=U'} 
                                        alt={vision.userDisplayName} 
                                        className="w-full h-full rounded-full object-cover p-[2px] bg-slate-950" 
                                    />
                                </div>
                                <span className="text-[11px] font-medium text-gray-400 group-hover:text-white transition-colors truncate max-w-[72px] text-center">
                                    {vision.userDisplayName || 'Anonymous'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedVisionIndex !== null && (
                <VisionViewer
                    visions={visions}
                    startIndex={selectedVisionIndex}
                    onClose={closeViewer}
                    onNextUser={handleNextUser}
                    onPrevUser={handlePrevUser}
                    handleUserSelect={handleUserSelect}
                />
            )}

            {creatorOpen && (
                <VisionCreator 
                    onClose={() => setCreatorOpen(false)} 
                />
            )}
        </>
    );
}

export default VisionsPanel;
